import React, { useState, useEffect, useRef } from 'react'
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { resolveFlowColor } from '../../core/domain/flowColors'
import type { Flow, FlowPreferenceStatus } from '../../core/domain/models'
import { resolveFlowPreferenceStatus } from '../../core/domain/flowPreferenceStatus'
import {
  BanIcon,
  ChevronDownIcon,
  MinusIcon,
  PencilIcon,
  PlusIcon,
  StarFilledIcon,
  TrashIcon,
} from '../components/Icons'
import { ItemFlowPreferences } from '../components/ItemFlowPreferences'
import { getAvailableFlowsForItem } from '../../core/domain/flowPreferenceStatus'
import { sortableItemPropsEqual } from './sortableItemMemo'

// Stable no-op function for React.memo'd components (avoids new references each render)
export const noop = () => {}

export interface SortableItemProps {
    id: string;
    item: any;
    completing: boolean;
    onToggle: (id: string, checked: boolean, name: string) => void;
    isExpanded: boolean;
    onToggleExpand: (id: string | null) => void;
    onDelete: (id: string, name: string) => void;
    onStartRename?: (id: string) => void;
    onSubmitRename?: (id: string, newName: string, oldName: string) => void;
    isRenaming?: boolean;
    quantityDraft?: number;
    onUpdateQuantityDraft?: (id: string, quantity: number) => void;
    onSubmitQuantity?: (itemId: string, updateMetaItem: boolean) => void;
    isUnordered?: boolean;
    ranking?: number;
    isSeparator?: boolean;
    isGhost?: boolean;
    isHidden?: boolean;
    justAdded?: boolean;
    flows?: Flow[];
    selectedFlowId?: string;
    onToggleFlowAvailability?: (itemId: string, flowId: string, isAvailable: boolean) => void;
    onFlowPreferencesCommit?: (itemId: string, changes: Record<string, FlowPreferenceStatus>) => void;
    isFadingOut?: boolean;
}

export function AnimatedOptionsPanel({ isOpen, onClick, children }: { isOpen: boolean; onClick?: React.MouseEventHandler<HTMLDivElement>; children: React.ReactNode }) {
  const [shouldRenderChildren, setShouldRenderChildren] = useState(isOpen)

  useEffect(() => {
    if (isOpen) return
    if (!shouldRenderChildren) return

    // Fallback for environments where CSS transition events don't fire (e.g. tests).
    const timeoutId = window.setTimeout(() => setShouldRenderChildren(false), 220)
    return () => window.clearTimeout(timeoutId)
  }, [isOpen, shouldRenderChildren])

  useEffect(() => {
    if (isOpen) setShouldRenderChildren(true)
  }, [isOpen])

  return (
    <div
      className={`item-options-panel ${isOpen ? 'is-open' : 'is-closed'}`}
      aria-hidden={!isOpen}
      onClick={onClick}
      onTransitionEnd={(e) => {
        if (e.currentTarget !== e.target) return
        if (!isOpen) setShouldRenderChildren(false)
      }}
    >
      <div className="item-options-panel-inner">
        {shouldRenderChildren ? children : null}
      </div>
    </div>
  )
}

export function ItemNameWithQuantity({ name, quantity }: { name: string; quantity: number }) {
  return <>{quantity > 1 ? `${quantity}x ` : ''}{name}</>
}

export function InlineRenameInput({ currentName, onSubmit, onCancel }: { currentName: string; onSubmit: (newName: string) => void; onCancel: () => void }) {
  const [value, setValue] = useState(currentName)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    inputRef.current?.focus()
    inputRef.current?.select()
  }, [])

  const handleSubmit = () => {
    const trimmed = value.trim()
    if (trimmed && trimmed !== currentName) {
      onSubmit(trimmed)
    } else {
      onCancel()
    }
  }

  return (
    <input
      ref={inputRef}
      className="item-rename-input"
      type="text"
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault()
          handleSubmit()
        } else if (e.key === 'Escape') {
          e.preventDefault()
          onCancel()
        }
      }}
      onBlur={handleSubmit}
      onClick={(e) => e.stopPropagation()}
      aria-label="Rename item"
      maxLength={200}
    />
  )
}

const MAX_QUANTITY = 999

export const SortableItem = React.memo(function SortableItem({ id, item, completing, onToggle, isExpanded, onToggleExpand, onDelete, onStartRename, onSubmitRename, isRenaming, quantityDraft, onUpdateQuantityDraft, onSubmitQuantity, isUnordered, isSeparator, isGhost, isHidden, justAdded, flows, selectedFlowId, onFlowPreferencesCommit, isFadingOut }: SortableItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
      id,
      disabled: isSeparator || isGhost // Disable dragging for separator and ghosts
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isFadingOut ? 'opacity 500ms ease-out' : transition,
    display: isHidden ? 'none' : undefined,
    opacity: isFadingOut ? 0 : undefined,
  };
  
  if (isSeparator) {
      return (
        <div ref={setNodeRef} style={style} className="list-flow-separator">
          <h3 className="list-flow-section-title">{item.name}</h3>
          {item?.hint && (
          <div className="list-flow-boundary-hint">{item.hint}</div>
          )}
        </div>
      );
  }

  const currentQuantity = typeof item.quantity === 'number' ? item.quantity : 1
  const effectiveDraftQuantity = typeof quantityDraft === 'number' ? quantityDraft : currentQuantity
  const isQuantityDirty = effectiveDraftQuantity !== currentQuantity

  const availableFlows = flows ? getAvailableFlowsForItem(item.id, flows) : [];
  const isAvailableInAllFlows = flows ? availableFlows.length === flows.length : true;

  // Check if item is preferred on currently selected flow
  const selectedFlowStatus = selectedFlowId
    ? resolveFlowPreferenceStatus(flows?.find(f => f.id === selectedFlowId)?.rankings[item.id])
    : 'normal'
  const isPreferredOnSelectedFlow = selectedFlowStatus === 'preferred'

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`sortable-item-wrapper ${isDragging ? 'sortable-placeholder' : ''} ${isFadingOut ? 'fading-out' : ''}`}
    >
      <div className="item-container">
        <div
          data-item-id={item.id}
          className={`item-card ${completing ? ' completing' : ''} ${isDragging ? 'sortable-placeholder' : ''} ${isUnordered ? 'list-flow-unordered' : ''} ${isGhost ? 'list-flow-ghost' : ''} ${isExpanded ? 'expanded' : ''} ${justAdded ? 'just-added' : ''}`}
          onClick={() => {
            if (isGhost) return
            if (isDragging) return
            onToggleExpand(isExpanded ? null : item.id)
          }}
          ref={setActivatorNodeRef}
          {...attributes}
          {...listeners}
          style={{ width: '100%', touchAction: isDragging ? 'none' : 'manipulation' }}
        >
          <div className="item-checkbox" onClick={(e) => e.stopPropagation()}>
            <input
              type="checkbox"
              checked={item.isCompleted}
              onChange={() => !isGhost && onToggle(item.id, item.isCompleted, item.name)}
              disabled={isGhost}
            />
          </div>
          <div className="item-name">
            {isRenaming ? (
              <InlineRenameInput
                currentName={item.name}
                onSubmit={(newName) => onSubmitRename?.(item.id, newName, item.name)}
                onCancel={() => onSubmitRename?.(item.id, item.name, item.name)}
              />
            ) : (
              <>
                {isPreferredOnSelectedFlow && (
                  <span className="item-preferred-star" title="Preferred in this flow">
                    <StarFilledIcon size={14} />
                  </span>
                )}
                <ItemNameWithQuantity name={item.name} quantity={currentQuantity} />
                {!isAvailableInAllFlows && availableFlows.length > 0 && (
                  <div className="item-flow-dots">
                    {availableFlows.map(f => (
                      <span key={f.id} className="item-flow-dot" style={{ backgroundColor: `var(--flow-accent-${resolveFlowColor(f)})` }} title={`Available in ${f.name}`} />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
          {!isGhost && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onToggleExpand(isExpanded ? null : item.id);
              }}
              className="item-expand-btn"
              title={isExpanded ? "Hide options" : "Show options"}
              aria-label={isExpanded ? "Hide options" : "Show options"}
              aria-expanded={isExpanded}
            >
              <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`} aria-hidden="true">
                <ChevronDownIcon />
              </span>
            </button>
          )}
        </div>
        {!isGhost && (
          <AnimatedOptionsPanel isOpen={isExpanded} onClick={(e) => e.stopPropagation()}>
            <div className="item-options-left">
              {flows && flows.length > 0 && (
                <ItemFlowPreferences
                  itemId={item.id}
                  flows={flows}
                  selectedFlowId={selectedFlowId}
                  onCommit={onFlowPreferencesCommit || (() => {})}
                  commitMode="on-collapse"
                  isOpen={isExpanded}
                />
              )}
              <div className="item-action-buttons">
              {onStartRename && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onStartRename(item.id);
                    onToggleExpand(null);
                  }}
                  className="item-option-btn rename"
                  title="Rename"
                  aria-label="Rename"
                >
                  <PencilIcon />
                </button>
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(item.id, item.name);
                  onToggleExpand(null);
                }}
                className="item-option-btn delete"
                title="Delete"
                aria-label="Delete"
              >
                <TrashIcon />
              </button>
              </div>
            </div>

            <div className="item-options-right">
              <div className="item-quantity-row">
                <span className="item-quantity-label">Qty:</span>
                <div className="item-quantity-stepper">
                  <button
                    type="button"
                    className="item-quantity-btn"
                    aria-label="Decrease quantity"
                    title="Decrease quantity"
                    disabled={effectiveDraftQuantity <= 1}
                    onClick={(e) => {
                      e.stopPropagation()
                      onUpdateQuantityDraft?.(item.id, Math.max(1, effectiveDraftQuantity - 1))
                    }}
                  >
                    <MinusIcon />
                  </button>

                  <input
                    className="item-quantity-input"
                    aria-label="Quantity input"
                    title="Quantity"
                    type="number"
                    min={1}
                    value={effectiveDraftQuantity}
                    onChange={(e) => {
                      e.stopPropagation()
                      const raw = e.target.value
                      const next = raw === '' ? 1 : Number(raw)
                      if (!Number.isFinite(next)) return
                      onUpdateQuantityDraft?.(item.id, Math.max(1, Math.floor(next)))
                    }}
                  />

                  <button
                    type="button"
                    className="item-quantity-btn"
                    aria-label="Increase quantity"
                    title="Increase quantity"
                    disabled={effectiveDraftQuantity >= MAX_QUANTITY}
                    onClick={(e) => {
                      e.stopPropagation()
                      onUpdateQuantityDraft?.(item.id, Math.min(MAX_QUANTITY, effectiveDraftQuantity + 1))
                    }}
                  >
                    <PlusIcon />
                  </button>
                </div>
              </div>

              <div className="item-quantity-scope">
                <button
                  type="button"
                  className="item-quantity-scope-btn"
                  aria-label="Apply once"
                  title="Change quantity for this list only"
                  disabled={!isQuantityDirty}
                  onClick={(e) => {
                    e.stopPropagation()
                    onSubmitQuantity?.(item.id, false)
                  }}
                >
                  Apply once
                </button>
                <button
                  type="button"
                  className="item-quantity-scope-btn scope-btn-remember"
                  aria-label="Remember quantity"
                  title="Change quantity and remember for next time"
                  disabled={!isQuantityDirty}
                  onClick={(e) => {
                    e.stopPropagation()
                    onSubmitQuantity?.(item.id, true)
                  }}
                >
                  Remember
                </button>
              </div>
            </div>
          </AnimatedOptionsPanel>
        )}
      </div>
    </div>
  );
}, sortableItemPropsEqual)


export const FlowModeUnavailableItem = React.memo(function FlowModeUnavailableItem({ item }: { item: any }) {
  return (
    <div className="item-card list-flow-unavailable" data-item-id={item.id} aria-disabled="true">
      <div className="item-name">{item.name}</div>
      <span className="list-flow-unavailable-badge" aria-label="Not available in this flow" title="Not available in this flow">
        <BanIcon />
        <span className="list-flow-unavailable-badge-text">Not available</span>
      </span>
    </div>
  )
})
